/*
  ItaCoverageWarningModal — pre-rollout guard for ITAT templates.

  Rollout only creates test steps from controls wired to each in-scope
  asset/application via ctr_implementation_steps (implementation_type
  IT_ASSET / IT_APPLICATION). Targets with zero wired controls are silently
  skipped by the backend, which confuses users because the control register
  and the ITAT template live on different screens. This modal surfaces those
  gaps BEFORE rollout, explains how to link controls, and still lets the
  user proceed.
*/
import React from 'react';
import { AlertTriangle, Rocket } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { ItaTemplateTarget } from '../../services/itaService';

const ItaCoverageWarningModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    gaps: ItaTemplateTarget[];
    totalTargets: number;
    templateName?: string;
    proceeding?: boolean;
    onProceed: () => void;
}> = ({ isOpen, onClose, gaps, totalTargets, templateName, proceeding, onProceed }) => {
    const allEmpty = gaps.length >= totalTargets;
    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md" closeOnOverlayClick={false} data-testid="MOD_ita_coverage_warning">
            <Modal.Header>Roll out with unlinked controls?</Modal.Header>
            <Modal.Body>
                <div className="flex flex-col gap-y-4">
                    <div className="flex items-start gap-x-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                        <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-600" />
                        <p className="text-sm text-amber-800">
                            {allEmpty
                                ? <>None of the assets/applications in scope of{' '}
                                    {templateName ? <>"<span className="font-medium">{templateName}</span>"</> : 'this template'} have
                                    any controls linked to them. Rolling out now will create <span className="font-medium">no test steps</span> —
                                    the rollout will be rejected or the allotted work will be empty.</>
                                : <>{gaps.length} of {totalTargets} assets/applications in scope of{' '}
                                    {templateName ? <>"<span className="font-medium">{templateName}</span>"</> : 'this template'} have
                                    no controls linked to them. These targets will be <span className="font-medium">skipped</span> during
                                    rollout — no test program is created for them.</>}
                        </p>
                    </div>

                    <div>
                        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Targets without linked controls
                        </p>
                        <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                            {gaps.map(g => (
                                <li key={`${g.target_type}-${g.target_id}`} className="flex items-center justify-between px-3 py-2">
                                    <span className="text-sm text-gray-700">
                                        {g.target_code ? `${g.target_code} — ` : ''}{g.target_label || `#${g.target_id}`}
                                    </span>
                                    <span className={`status-badge sm ${g.target_type === 'IT_ASSET' ? 'info' : 'neutral'}`}>
                                        {g.target_type === 'IT_ASSET' ? 'IT Asset' : 'IT Application'}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="rounded-lg bg-gray-50 p-3">
                        <p className="text-xs font-semibold text-gray-700">How to link a control to an asset/application</p>
                        <ol className="mt-1 list-decimal space-y-0.5 pl-4 text-xs text-gray-600">
                            <li>Open <span className="font-medium">Internal Controls</span> and select the control.</li>
                            <li>Go to the <span className="font-medium">Implementations</span> tab and click <span className="font-medium">Create</span>.</li>
                            <li>Choose type <span className="font-medium">IT Asset</span> or <span className="font-medium">IT Application</span> and pick the target listed above.</li>
                        </ol>
                        <p className="mt-1.5 text-xs italic text-gray-500">
                            Only controls with an IT Asset / IT Application implementation become test steps in the rolled-out program.
                        </p>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="fill"
                    icon={Rocket}
                    onClick={onProceed}
                    disabled={proceeding}
                    data-testid="BTN_ita_coverage_proceed"
                >
                    Roll Out Anyway
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ItaCoverageWarningModal;
